import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import useArrowNavigation from '../Hooks/useArrowNavigation';
import TypeaheadInput from './input';
import { filterTypeaheadItems, normalizeTypeaheadItems } from './utils';
const lodashUtil = require('lodash/util');

/**
 * @param {Array} items - An array of items to display in the dropdown.
 * Each item should be an object with the following properties:
 * - `value` (string, required): The value of the item.
 * - `label` (string, required): The label to display for the item.
 * - `category` (string, optional): The optional category for the item.
 *
 * @param {Function} onSelect - Function called when an item is selected.
 * @param {boolean} [openOnFocus=true] - If true, the dropdown opens when the input is focused, otherwise it opens when the user starts typing.
 * @param {string} [label] - The label for the input field.
 * @param {string} [placeholderText] - Placeholder inside of the input field.
 *
 * @param {string} [tooltipTitle] - The heading displayed in the tooltip.
 * @param {string} [tooltipText] - The text displayed inside of the tooltip.
 * @param {string} [tooltipLinkText] - The link-text displayed next to the label.
 * @param {bool} [shouldSearchCategories] - Whether the categories should be included in the search or not
 * @param {string} [defaultValue] - The default value to be selected in the typeahead.
 */
const Typeahead = ({
  items,
  onSelect,
  openOnFocus = true,
  label,
  placeholderText,
  tooltipText,
  tooltipLinkText,
  translations,
  inputRef,
  rightAlignedTooltip,
  shouldSearchCategories = false,
  defaultValue = '',
}) => {
  const [query, setQuery] = useState('');
  const [allItems, setAllItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const localInputRef = useRef(null);
  const ref = inputRef || localInputRef;
  const uniqueId = useRef(lodashUtil.uniqueId('typeahead-input-')).current;

  const flattenedListItems = filteredItems.flatMap(
    (category) => category.items
  );

  const maxIndex = flattenedListItems.length - 1;

  const getItemByIndex = (index) => flattenedListItems[index];

  const { highlightedIndex, resetIndex, handleKeyDown } = useArrowNavigation(
    maxIndex,
    {
      isOpen: isDropdownOpen,
      onItemSelect: (item) => {
        setQuery(item.label);
        setIsDropdownOpen(false);
        resetIndex();
        onSelect && onSelect(item);
      },
      onClose: () => {
        setIsDropdownOpen(false);
        resetIndex();
      },
      getItemByIndex,
    }
  );

  const highlightedItemValue = flattenedListItems[highlightedIndex]?.value;

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    const newFilteredItems = filterTypeaheadItems(
      allItems,
      value,
      shouldSearchCategories
    );
    setFilteredItems(newFilteredItems);
    setIsDropdownOpen(true);
    resetIndex();
  };

  const handleInputFocus = () => {
    if (openOnFocus || (!openOnFocus && query.length >= 1)) {
      ref?.current.focus();
      setIsDropdownOpen(true);
      setFilteredItems(allItems);
    }
  };

  const handleItemClick = (item) => {
    setQuery(item.label);
    setIsDropdownOpen(false);
    resetIndex();
    onSelect && onSelect(item);
  };

  const handleTooltipClick = () => {
    setIsTooltipVisible(!isTooltipVisible);
  };

  const handleOnBlur = (e) => {
    e.preventDefault();
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    const normalizedItems = normalizeTypeaheadItems(items);
    setAllItems(normalizedItems);
    setFilteredItems(normalizedItems);
  }, [items]);

  useEffect(() => {
    if (defaultValue) {
      const selectedItem = allItems
        .flatMap((category) => category.items)
        .find((item) => item.value === defaultValue);
      if (selectedItem) {
        setQuery(selectedItem.label);
      }
    }
  }, [defaultValue, allItems]);

  return items && items.length > 0 ? (
    <TypeaheadInput
      inputRef={ref}
      id={uniqueId}
      query={query}
      label={label}
      placeholderText={placeholderText}
      isDropdownOpen={isDropdownOpen}
      filteredItems={filteredItems}
      onQueryChange={handleInputChange}
      onFocus={handleInputFocus}
      onKeyDown={handleKeyDown}
      onItemClick={handleItemClick}
      onBlur={handleOnBlur}
      tooltipLinkText={tooltipLinkText}
      tooltipText={tooltipText}
      rightAlignedTooltip={rightAlignedTooltip}
      handleTooltipClick={handleTooltipClick}
      highlightedItemValue={highlightedItemValue}
      flattenedListItems={flattenedListItems}
      translations={translations}
    />
  ) : null;
};

Typeahead.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      category: PropTypes.string,
    })
  ).isRequired,
  onSelect: PropTypes.func,
  openOnFocus: PropTypes.bool,
  label: PropTypes.string,
  placeholderText: PropTypes.string,
  tooltipText: PropTypes.string,
  tooltipLinkText: PropTypes.string,
  defaultValue: PropTypes.string,
};

export default Typeahead;
